/**
 * SocketIO Context & Provider
 *
 * React context for managing socket connection state.
 * Connects automatically when user is authenticated.
 */

import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

import { socketioService } from './socketioService';

import type { ReactNode } from 'react';
import type { SocketIOConnectionStatus, SocketIOEventData } from './types';

// ============================================================================
// Context Types
// ============================================================================

interface SocketIOContextValue {
  /** Current connection status */
  status: SocketIOConnectionStatus;
  /** Whether socket is connected */
  isConnected: boolean;
  /** Connect to socket with auth token */
  connect: (token: string) => Promise<void>;
  /** Disconnect from socket */
  disconnect: () => void;
  /** Subscribe to socket events */
  subscribe: (callback: (event: SocketIOEventData) => void) => () => void;
  /** List of connected namespaces */
  connectedNamespaces: string[];
}

// ============================================================================
// Context
// ============================================================================

const SocketIOContext = createContext<SocketIOContextValue | null>(null);

// ============================================================================
// Provider Props
// ============================================================================

interface SocketIOProviderProps {
  children: ReactNode;
  /**
   * Auth token for socket connection.
   * When provided, socket will auto-connect.
   * When null/undefined, socket will disconnect.
   */
  authToken?: string | null;
}

// ============================================================================
// Provider Component
// ============================================================================

export function SocketIOProvider({ children, authToken }: SocketIOProviderProps) {
  const [status, setStatus] = useState<SocketIOConnectionStatus>('disconnected');
  const [connectedNamespaces, setConnectedNamespaces] = useState<string[]>([]);

  // --------------------------------------------------------------------------
  // Connection Management
  // --------------------------------------------------------------------------

  const connect = useCallback(async (token: string) => {
    try {
      await socketioService.connect(token);
    } catch (error) {
      console.error('[SocketIOProvider] Failed to connect:', error);
    }
  }, []);

  const disconnect = useCallback(() => {
    socketioService.disconnect();
  }, []);

  const subscribe = useCallback((callback: (event: SocketIOEventData) => void) => {
    return socketioService.subscribe(callback);
  }, []);

  // --------------------------------------------------------------------------
  // Auto-connect when authToken changes
  // --------------------------------------------------------------------------

  useEffect(() => {
    if (authToken) {
      console.log('[SocketIOProvider] Auth token provided, connecting...');
      connect(authToken);
    } else {
      console.log('[SocketIOProvider] No auth token, disconnecting...');
      disconnect();
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [authToken, connect, disconnect]);

  // --------------------------------------------------------------------------
  // Subscribe to status changes
  // --------------------------------------------------------------------------

  useEffect(() => {
    const unsubscribe = socketioService.onStatusChange((newStatus) => {
      setStatus(newStatus);
      setConnectedNamespaces(socketioService.connectedNamespaces);
    });

    return unsubscribe;
  }, []);

  // --------------------------------------------------------------------------
  // Context Value
  // --------------------------------------------------------------------------

  const value = useMemo<SocketIOContextValue>(
    () => ({
      status,
      isConnected: status === 'connected',
      connect,
      disconnect,
      subscribe,
      connectedNamespaces,
    }),
    [status, connect, disconnect, subscribe, connectedNamespaces]
  );

  return <SocketIOContext.Provider value={value}>{children}</SocketIOContext.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

export function useSocketIOContext(): SocketIOContextValue {
  const context = useContext(SocketIOContext);
  if (!context) {
    throw new Error('useSocketIOContext must be used within a SocketIOProvider');
  }
  return context;
}
